import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Plus, Edit, Trash2, Save, X, Key, Search, User, Lock, Eye, EyeOff, Hash, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { getProductKeys, addProductKey, updateProductKey, deleteProductKey } from '../api';
import { toast } from '../hooks/use-toast';

const ProductKey = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [showPasswords, setShowPasswords] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [newRowExpanded, setNewRowExpanded] = useState(false);
  const [newRow, setNewRow] = useState({ customerName: '', productName: '', purchaseDate: '', code: '', username: '', password: '', authType: 'code', serialNumber: '', ipAddress: '' });
  const [editItem, setEditItem] = useState({ customerName: '', productName: '', purchaseDate: '', code: '', username: '', password: '', authType: 'code', serialNumber: '', ipAddress: '' });

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const data = await getProductKeys();
      setItems(data);
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לטעון את מפתחות המוצר', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newRow.customerName || !newRow.productName) {
      toast({ title: 'שגיאה', description: 'נא למלא שם לקוח ושם מוצר', variant: 'destructive' });
      return;
    }
    if (newRow.authType === 'code' && !newRow.code) {
      toast({ title: 'שגיאה', description: 'נא למלא קוד מוצר', variant: 'destructive' });
      return;
    }
    if (newRow.authType === 'credentials' && (!newRow.username || !newRow.password)) {
      toast({ title: 'שגיאה', description: 'נא למלא שם משתמש וסיסמה', variant: 'destructive' });
      return;
    }
    try {
      const added = await addProductKey(newRow);
      setItems([...items, added]);
      setNewRow({ customerName: '', productName: '', purchaseDate: '', code: '', username: '', password: '', authType: 'code', serialNumber: '', ipAddress: '' });
      setNewRowExpanded(false);
      toast({ title: 'נשמר', description: 'מפתח מוצר נוסף בהצלחה' });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן להוסיף מפתח מוצר', variant: 'destructive' });
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditItem({
      customerName: item.customerName, productName: item.productName, purchaseDate: item.purchaseDate,
      code: item.code || '', username: item.username || '', password: item.password || '',
      authType: item.authType || (item.username ? 'credentials' : 'code'),
      serialNumber: item.serialNumber || '', ipAddress: item.ipAddress || ''
    });
    if (item.serialNumber || item.ipAddress) {
      setExpandedRows(prev => ({ ...prev, [item.id]: true }));
    }
  };

  const handleUpdate = async (id) => {
    if (!editItem.customerName || !editItem.productName) {
      toast({ title: 'שגיאה', description: 'נא למלא שם לקוח ושם מוצר', variant: 'destructive' });
      return;
    }
    try {
      const updated = await updateProductKey(id, editItem);
      setItems(items.map(item => item.id === id ? updated : item));
      setEditingId(null);
      toast({ title: 'הצלחה', description: 'מפתח מוצר עודכן בהצלחה' });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן מפתח מוצר', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק מפתח מוצר זה?')) {
      try {
        await deleteProductKey(id);
        setItems(items.filter(item => item.id !== id));
        toast({ title: 'הצלחה', description: 'מפתח מוצר נמחק בהצלחה' });
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן למחוק מפתח מוצר', variant: 'destructive' });
      }
    }
  };

  const togglePasswordVisibility = (id) => setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleRowExpand = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const uniqueCustomers = [...new Set(items.map(item => item.customerName))].sort();
  const filteredItems = searchCustomer ? items.filter(item => item.customerName === searchCustomer) : items;

  const AuthTypeToggle = ({ value, onChange, testIdPrefix }) => (
    <div className="flex rounded-md overflow-hidden border" data-testid={`${testIdPrefix}-auth-toggle`}>
      <button type="button" onClick={() => onChange('code')}
        className={`px-3 py-1.5 text-xs font-medium transition-colors ${value === 'code' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        data-testid={`${testIdPrefix}-toggle-code`}>
        <Key className="w-3 h-3 inline ml-1" />קוד
      </button>
      <button type="button" onClick={() => onChange('credentials')}
        className={`px-3 py-1.5 text-xs font-medium transition-colors ${value === 'credentials' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        data-testid={`${testIdPrefix}-toggle-credentials`}>
        <User className="w-3 h-3 inline ml-1" />משתמש
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" dir="rtl">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
              <Key className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800">מפתח מוצר</h1>
          </div>
        </div>

        {/* Filter */}
        <div className="max-w-6xl mx-auto mb-6">
          <Card className="shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Search className="w-5 h-5 text-gray-500" />
                <label className="font-medium text-gray-700">סינון לפי לקוח:</label>
                <select value={searchCustomer} onChange={(e) => setSearchCustomer(e.target.value)}
                  className="flex-1 max-w-xs px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500" data-testid="customer-filter">
                  <option value="">כל הלקוחות</option>
                  {uniqueCustomers.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {searchCustomer && (
                  <Button onClick={() => setSearchCustomer('')} variant="outline" size="sm" className="text-gray-600">
                    <X className="w-4 h-4 ml-1" />נקה סינון
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <div className="max-w-6xl mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Key className="w-6 h-6" />
                רשימת מפתחות מוצר ({filteredItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-gray-600"><p className="text-lg">טוען...</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="product-key-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">שם לקוח</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">תאריך קנייה</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">שם מוצר</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">סוג</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">קוד / פרטי התחברות</th>
                        <th className="px-4 py-4 text-center text-sm font-bold text-gray-700">פעולות</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredItems.map((item) => {
                        const itemAuthType = item.authType || (item.username ? 'credentials' : 'code');
                        const isExpanded = expandedRows[item.id];
                        const hasHwInfo = item.serialNumber || item.ipAddress;
                        return (
                          <React.Fragment key={item.id}>
                            <tr className="hover:bg-gray-50" data-testid={`product-key-row-${item.id}`}>
                              {editingId === item.id ? (
                                <>
                                  <td className="px-4 py-3">
                                    <Input value={editItem.customerName} onChange={(e) => setEditItem({ ...editItem, customerName: e.target.value })} data-testid="edit-customer-name" />
                                  </td>
                                  <td className="px-4 py-3">
                                    <Input value={editItem.purchaseDate} onChange={(e) => setEditItem({ ...editItem, purchaseDate: e.target.value })} type="date" data-testid="edit-purchase-date" />
                                  </td>
                                  <td className="px-4 py-3">
                                    <Input value={editItem.productName} onChange={(e) => setEditItem({ ...editItem, productName: e.target.value })} data-testid="edit-product-name" />
                                  </td>
                                  <td className="px-4 py-3">
                                    <AuthTypeToggle value={editItem.authType} onChange={(v) => setEditItem({ ...editItem, authType: v })} testIdPrefix="edit" />
                                  </td>
                                  <td className="px-4 py-3">
                                    {editItem.authType === 'code' ? (
                                      <Input value={editItem.code} onChange={(e) => setEditItem({ ...editItem, code: e.target.value })} className="font-mono" placeholder="קוד מוצר..." data-testid="edit-code" />
                                    ) : (
                                      <div className="flex gap-2">
                                        <Input value={editItem.username} onChange={(e) => setEditItem({ ...editItem, username: e.target.value })} placeholder="שם משתמש..." className="flex-1" data-testid="edit-username" />
                                        <Input value={editItem.password} onChange={(e) => setEditItem({ ...editItem, password: e.target.value })} placeholder="סיסמה..." className="flex-1" data-testid="edit-password" />
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex gap-2 justify-center">
                                      <Button onClick={() => toggleRowExpand(item.id)} size="sm" variant="outline" className="text-indigo-600" data-testid="edit-toggle-hw">
                                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                      </Button>
                                      <Button onClick={() => handleUpdate(item.id)} size="sm" className="bg-green-500 hover:bg-green-600" data-testid="save-edit-button">
                                        <Save className="w-4 h-4" />
                                      </Button>
                                      <Button onClick={() => setEditingId(null)} size="sm" variant="outline" data-testid="cancel-edit-button">
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="px-4 py-3 text-gray-800 font-medium">{item.customerName}</td>
                                  <td className="px-4 py-3 text-gray-600">
                                    {item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString('he-IL') : '-'}
                                  </td>
                                  <td className="px-4 py-3 text-gray-800">{item.productName}</td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${itemAuthType === 'credentials' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                      {itemAuthType === 'credentials' ? <><User className="w-3 h-3" /> משתמש</> : <><Key className="w-3 h-3" /> קוד</>}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    {itemAuthType === 'credentials' ? (
                                      <div className="flex items-center gap-3">
                                        <span className="text-sm">
                                          <User className="w-3.5 h-3.5 inline text-gray-400 ml-1" />
                                          <span className="font-mono text-blue-700">{item.username}</span>
                                        </span>
                                        <span className="text-sm flex items-center gap-1">
                                          <Lock className="w-3.5 h-3.5 inline text-gray-400" />
                                          <span className="font-mono text-blue-700">{showPasswords[item.id] ? item.password : '••••••••'}</span>
                                          <button onClick={() => togglePasswordVisibility(item.id)} className="text-gray-400 hover:text-gray-600 p-0.5" data-testid={`toggle-password-${item.id}`}>
                                            {showPasswords[item.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                          </button>
                                        </span>
                                      </div>
                                    ) : (
                                      <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono text-purple-700">{item.code}</code>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex gap-2 justify-center">
                                      <Button onClick={() => toggleRowExpand(item.id)} size="sm" variant="outline"
                                        className={`${hasHwInfo ? 'text-indigo-600 border-indigo-300' : 'text-gray-400'}`}
                                        data-testid={`expand-button-${item.id}`}>
                                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                      </Button>
                                      <Button onClick={() => handleEdit(item)} size="sm" variant="outline" data-testid={`edit-button-${item.id}`}>
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button onClick={() => handleDelete(item.id)} size="sm" variant="outline" className="text-red-600 hover:bg-red-50" data-testid={`delete-button-${item.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                            {/* Expanded row for Serial + IP */}
                            {isExpanded && (
                              <tr className="bg-indigo-50" data-testid={`expanded-row-${item.id}`}>
                                <td colSpan="6" className="px-6 py-3">
                                  <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                      <Hash className="w-4 h-4 text-indigo-500" />
                                      <span className="text-sm font-medium text-gray-600">מס' סידורי:</span>
                                      {editingId === item.id ? (
                                        <Input value={editItem.serialNumber} onChange={(e) => setEditItem({ ...editItem, serialNumber: e.target.value })}
                                          placeholder="הזן מס' סידורי..." className="w-52 font-mono text-sm h-8" data-testid="edit-serial-number" />
                                      ) : (
                                        <span className="font-mono text-sm text-indigo-700 bg-white px-2 py-0.5 rounded border">
                                          {item.serialNumber || '-'}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Globe className="w-4 h-4 text-indigo-500" />
                                      <span className="text-sm font-medium text-gray-600">מס' IP:</span>
                                      {editingId === item.id ? (
                                        <Input value={editItem.ipAddress} onChange={(e) => setEditItem({ ...editItem, ipAddress: e.target.value })}
                                          placeholder="הזן כתובת IP..." className="w-52 font-mono text-sm h-8" data-testid="edit-ip-address" />
                                      ) : (
                                        <span className="font-mono text-sm text-indigo-700 bg-white px-2 py-0.5 rounded border">
                                          {item.ipAddress || '-'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}

                      {/* New Row */}
                      <tr className="bg-green-50 hover:bg-green-100 transition-colors" data-testid="new-row">
                        <td className="px-4 py-3">
                          <Input value={newRow.customerName} onChange={(e) => setNewRow({ ...newRow, customerName: e.target.value })} placeholder="שם לקוח..." className="border-green-300 focus:border-green-500" data-testid="new-customer-name" />
                        </td>
                        <td className="px-4 py-3">
                          <Input value={newRow.purchaseDate} onChange={(e) => setNewRow({ ...newRow, purchaseDate: e.target.value })} type="date" className="border-green-300 focus:border-green-500" data-testid="new-purchase-date" />
                        </td>
                        <td className="px-4 py-3">
                          <Input value={newRow.productName} onChange={(e) => setNewRow({ ...newRow, productName: e.target.value })} placeholder="שם מוצר..." className="border-green-300 focus:border-green-500" data-testid="new-product-name" />
                        </td>
                        <td className="px-4 py-3">
                          <AuthTypeToggle value={newRow.authType} onChange={(v) => setNewRow({ ...newRow, authType: v })} testIdPrefix="new" />
                        </td>
                        <td className="px-4 py-3">
                          {newRow.authType === 'code' ? (
                            <Input value={newRow.code} onChange={(e) => setNewRow({ ...newRow, code: e.target.value })} placeholder="קוד מוצר..." className="border-green-300 focus:border-green-500 font-mono" data-testid="new-code" />
                          ) : (
                            <div className="flex gap-2">
                              <Input value={newRow.username} onChange={(e) => setNewRow({ ...newRow, username: e.target.value })} placeholder="שם משתמש..." className="border-green-300 focus:border-green-500 flex-1" data-testid="new-username" />
                              <Input value={newRow.password} onChange={(e) => setNewRow({ ...newRow, password: e.target.value })} placeholder="סיסמה..." className="border-green-300 focus:border-green-500 flex-1" data-testid="new-password" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-center">
                            <Button onClick={() => setNewRowExpanded(!newRowExpanded)} size="sm" variant="outline"
                              className={newRowExpanded ? 'text-indigo-600 border-indigo-300' : 'text-gray-400'}
                              data-testid="new-toggle-hw">
                              {newRowExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                            <Button onClick={handleAddItem} size="sm" className="bg-green-500 hover:bg-green-600 text-white" data-testid="add-button">
                              <Plus className="w-4 h-4 ml-1" />הוסף
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {/* New Row expanded for Serial + IP */}
                      {newRowExpanded && (
                        <tr className="bg-green-50" data-testid="new-expanded-row">
                          <td colSpan="6" className="px-6 py-3">
                            <div className="flex items-center gap-6">
                              <div className="flex items-center gap-2">
                                <Hash className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium text-gray-600">מס' סידורי:</span>
                                <Input value={newRow.serialNumber} onChange={(e) => setNewRow({ ...newRow, serialNumber: e.target.value })}
                                  placeholder="הזן מס' סידורי..." className="w-52 font-mono text-sm h-8 border-green-300" data-testid="new-serial-number" />
                              </div>
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium text-gray-600">מס' IP:</span>
                                <Input value={newRow.ipAddress} onChange={(e) => setNewRow({ ...newRow, ipAddress: e.target.value })}
                                  placeholder="הזן כתובת IP..." className="w-52 font-mono text-sm h-8 border-green-300" data-testid="new-ip-address" />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProductKey;
